import {
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_PHONE_HREF,
  ZoomBookingFrame,
  isEmbeddableBookingUrl,
} from '@nrx/booking-ui';
import type { ZoomBookingMember } from '@nrx/booking-ui';
import { Alert, Box, Link, Text } from '@nrx/ui';
import { type JSX, useState } from 'react';

import { notifyParent } from '../embed/embedded.js';

import { useReportBooking } from './booking-report.js';

type ConsultBookingFrameProps = {
  bookingUrl: string;
  member: ZoomBookingMember;
  title: string;
  /** Reported to an embedding care-betty page when the booking takes, whoever the caller is. */
  consultType: string;
  /**
   * Told when Zoom reports the booking took. The confirmation below is still rendered here — this
   * only lets a caller that offers a choice of calendars retire that choice, since an inline
   * "book with someone else" beside a success message reads like the booking did not land.
   */
  onBooked?: (() => void) | undefined;
};

/**
 * Wraps the shared Zoom booking iframe with a local "booked" confirmation and an
 * unresponsive-frame fallback.
 *
 * @remarks Callers must key this by `bookingUrl`: the booked/unresponsive flags are plain state and
 * `ZoomBookingFrame` arms its detection once per mount, so without a remount a swapped address
 * keeps the previous URL's outcome over a frame the member still needs.
 *
 * The confirmation is client-side. Zoom's booking id is also reported to the backend (the page's
 * {@link useReportBooking}), so CareBetty moves the member's consult to in-progress now; the backend
 * reads the booking back from Zoom before applying it, so the id is all the browser contributes.
 *
 * Both states announce themselves. The confirmation is `Alert tone="success"` (`role="status"`)
 * because the iframe swap was previously a silent DOM replacement, and the fallback is
 * `Alert tone="warning"` (`role="alert"`) because it only ever appears after the embed has failed
 * to respond — a member who cannot see the frame needs to be told the escape hatch exists (W4).
 *
 * ## The fallback has TWO shapes, and the difference is load-bearing
 *
 * This used to render `<Link href={bookingUrl}>` with the raw operator value and document itself as
 * opening "the same URL in a new tab". For the case the fallback was EXTENDED to cover — a
 * malformed, schemeless `bookingUrl` — that was false. A schemeless string is a relative URL, so
 * the browser resolved it against the app's own origin: the member landed on
 * `https://consult.<apex>/scheduler.zoom.us/init-page`, which the distribution's 403/404 →
 * `/index.html` rewrite serves as HTTP 200 with the SPA shell. A new tab showing the same broken app
 * is worse than a dead link, because it looks like it worked.
 *
 * So the link renders only when `bookingUrl` is a usable absolute http(s) URL — i.e. when the frame
 * really is dead rather than the address unopenable. Otherwise the member gets a support number and
 * the configured value is shown verbatim, so a support engineer reading a screenshot can see exactly
 * what the operator published.
 */
export function ConsultBookingFrame({
  bookingUrl,
  member,
  title,
  consultType,
  onBooked,
}: ConsultBookingFrameProps): JSX.Element {
  const [booked, setBooked] = useState(false);
  const [unresponsive, setUnresponsive] = useState(false);
  const reportBooking = useReportBooking();
  const linkable = isEmbeddableBookingUrl(bookingUrl);

  if (booked) {
    return (
      <Alert tone="success" className="consult-booking-outcome">
        <Text as="p" size="5" weight="bold">
          Your consult is booked!
        </Text>
        <Text as="p">Check your email for the confirmation and calendar invite.</Text>
      </Alert>
    );
  }

  return (
    <Box>
      <ZoomBookingFrame
        bookingUrl={bookingUrl}
        member={member}
        title={title}
        onBooked={({ scheduledEventId }) => {
          setBooked(true);
          notifyParent({ type: 'nrx-consult:booked', consultType });
          reportBooking?.(scheduledEventId);
          onBooked?.();
        }}
        onUnresponsive={() => setUnresponsive(true)}
      />
      {unresponsive ? (
        <Alert tone="warning" className="consult-booking-outcome">
          {linkable ? (
            <Text as="p">
              Having trouble loading the scheduler?{' '}
              <Link href={bookingUrl} target="_blank" rel="noopener noreferrer" underline>
                <Text weight="medium">Book in a new tab</Text>
              </Link>
            </Text>
          ) : (
            <>
              <Text as="p">
                We can&apos;t open the scheduler for this consult. Please call us at{' '}
                <Link href={SUPPORT_PHONE_HREF} underline>
                  <Text weight="medium">{SUPPORT_PHONE_DISPLAY}</Text>
                </Link>{' '}
                and we&apos;ll book it with you.
              </Text>
              <Text as="p" size="2" color="muted">
                Scheduler address on file: {bookingUrl}
              </Text>
            </>
          )}
        </Alert>
      ) : null}
    </Box>
  );
}
